import { formatAmount, type OrderStatus, type PackDto, type PayMethod, type StartPurchaseResponse } from '@app/shared';
import type { Dictionary } from '../i18n';

/** The waiting sheet asks about its order this often while it is open (spec 5.4). */
export const POLL_MS = 3_000;
/** …and stops after this long: the person hears from the bot if the money comes later. */
export const POLL_GIVE_UP_MS = 30 * 60_000;

const KASPI_PHONE_KEY = 'kaspi-phone';

type Currency = 'KZT' | 'RUB' | 'USD' | 'XTR';

const CURRENCY_OF: Record<PayMethod, Currency> = {
  kaspi: 'KZT',
  lava_card: 'RUB',
  lava_sbp: 'RUB',
  lava_usd: 'USD',
  stars: 'XTR',
};

function priceOf(pack: PackDto, method: PayMethod): number {
  const c = CURRENCY_OF[method];
  return c === 'KZT' ? pack.prices.kzt : c === 'RUB' ? pack.prices.rub : c === 'USD' ? pack.prices.usd : pack.prices.stars;
}

/** «7 490 ₸ · 1 490 ₽»: one price per currency of the offered methods. */
export function packPriceLine(pack: PackDto, _t: Dictionary): string {
  const seen = new Set<Currency>();
  const out: string[] = [];
  for (const m of pack.methods) {
    const c = CURRENCY_OF[m];
    if (seen.has(c)) continue;
    seen.add(c);
    out.push(formatAmount(priceOf(pack, m), c));
  }
  return out.join(' · ');
}

/** The rows of «Как оплатить», each method at its own price. */
export function methodRows(pack: PackDto, t: Dictionary): Array<{ method: PayMethod; label: string; price: string }> {
  return pack.methods.map((method) => ({
    method,
    label: t.purchase.methods[method],
    price: formatAmount(priceOf(pack, method), CURRENCY_OF[method]),
  }));
}

/** The Kaspi number stays on this phone only (spec 3.3); the server keeps a mask. */
export function rememberKaspiPhone(phone: string): void {
  try {
    localStorage.setItem(KASPI_PHONE_KEY, phone);
  } catch {
    // A private window or blocked storage: the number is typed again next time.
  }
}

export function rememberedKaspiPhone(): string {
  try {
    return localStorage.getItem(KASPI_PHONE_KEY) ?? '';
  } catch {
    return '';
  }
}

export type SheetState =
  | { step: 'methods' }
  | { step: 'kaspi' }
  | { step: 'email'; method: PayMethod }
  | {
      step: 'waiting';
      code: string;
      method: PayMethod;
      mask?: string;
      amount?: string;
      expiresAt?: string;
      /** The lava.top page: a tap opens it again. */
      url?: string;
      /** The Kaspi invoice call failed twice: the order is closed, yet a payment may still come. */
      unsure?: true;
    }
  | { step: 'paid'; code: string; credits: number; balance: number | null }
  | { step: 'closed' };

/** Where the sheet goes after the answer of POST /payments/orders, and what it says. */
export function nextState(
  state: SheetState,
  result: StartPurchaseResponse,
  ctx: { method: PayMethod; t: Dictionary },
): { state: SheetState; message?: string } {
  const p = ctx.t.purchase;
  switch (result.kind) {
    case 'kaspi_sent':
      return {
        state: { step: 'waiting', code: result.code, method: 'kaspi', mask: result.mask, amount: formatAmount(result.amount, 'KZT') },
      };
    case 'lava_link':
      return { state: { step: 'waiting', code: result.code, method: ctx.method, expiresAt: result.expiresAt, url: result.url } };
    case 'stars_link':
      return { state: { step: 'waiting', code: result.code, method: 'stars' } };
    case 'unsure':
      return { state: { step: 'waiting', code: result.code, method: 'kaspi', unsure: true }, message: p.unsure };
    case 'refused':
      return { state: { step: 'methods' }, message: p.refused(result.message) };
    case 'not_kaspi_client':
      return { state, message: p.notKaspiClient };
    case 'bad_phone':
      return { state, message: p.badPhone };
    case 'bad_email':
      return { state, message: p.badEmail };
    case 'kaspi_down':
      return { state: { step: 'methods' }, message: p.kaspiDown };
    case 'unavailable':
      return { state: { step: 'methods' }, message: p.unavailable };
    case 'too_many':
      return { state, message: p.tooMany };
    case 'error':
      return { state, message: p.error };
  }
}

/** What one poll of the waiting sheet means: done, or keep waiting. */
export function pollVerdict(state: SheetState, status: OrderStatus): 'paid' | 'failed' | 'expired' | 'wait' {
  if (status === 'paid') return 'paid';
  if (state.step === 'waiting' && state.unsure) return 'wait';
  if (status === 'failed' || status === 'expired') return status;
  return 'wait';
}

/** What Telegram pays the owner per star when they are withdrawn (spec 4.1). */
const USD_PER_STAR = 0.013;

/** «$16,90»: the hint under the Stars price in «Пакеты». */
export function starsUsdHint(stars: number): string {
  return `$${(Math.round(stars * USD_PER_STAR * 100) / 100).toFixed(2).replace('.', ',')}`;
}

// Asia/Almaty is UTC+5 all year.
const ALMATY_OFFSET_MS = 5 * 60 * 60 * 1000;

/** «12.10 14:30»: an order time in Almaty, whatever the phone's own zone. */
export function almatyTime(iso: string): string {
  const s = new Date(new Date(iso).getTime() + ALMATY_OFFSET_MS).toISOString();
  return `${s.slice(8, 10)}.${s.slice(5, 7)} ${s.slice(11, 16)}`;
}

/** A purchased lot expires in a hundred years: shown «без срока», never as a date. */
export function isForever(expiresAt: string | Date, now: Date = new Date()): boolean {
  return new Date(expiresAt).getTime() - now.getTime() > 50 * 365 * 86_400_000;
}
